export type Project = {
  id: string;
  /** Client name, or a label for non-case-study tiles */
  client: string;
  title: string;
  description: string;
  tags: string[];
  href: string;
  /**
   * "case-study" opens an internal case study page.
   * "reel" links out to a social video and shows its reach on the tile.
   * "invite" renders the more-work tile.
   * "in-progress" has a case study page but is featured under In Progress, not the work grid.
   */
  kind: "case-study" | "reel" | "invite" | "in-progress";
  /** Headline reach numbers shown on reel tiles */
  stats?: { value: string; label: string }[];
  /** Short credit line shown on reel tiles, e.g. what the work involved */
  credit?: string;
  image?: string;
  /** CSS mask-image marking where trees sway in the photo */
  motionMask?: string;
  motionStyle?: "foliage" | "branches" | "water";
  /** CSS object-position for the wide case study hero crop */
  heroPosition?: string;
  cta?: string;
};

export const projects: Project[] = [
  {
    id: "lawrence-responsible-ai",
    client: "Lawrence University",
    title: "Responsible AI, Before the Rules",
    description: "Building a human-centered framework for responsible AI exploration.",
    tags: ["AI Governance", "Responsible Tech"],
    href: "/work/lawrence-responsible-ai",
    kind: "case-study",
    image:
      "https://cdn.magicpatterns.com/uploads/tA1iU8HpjBuHVenTLtm4zf/about-hero.jpg",
    motionMask: "radial-gradient(ellipse 50% 62% at 66% 62%, #000 60%, transparent 100%)",
    motionStyle: "foliage",
  },
  {
    id: "lawrence-website",
    client: "Lawrence University",
    title: "A Website That Keeps Learning",
    description: "10,000+ pages transformed into a focused, evolving web product.",
    tags: ["Digital Transformation", "Web Product"],
    href: "/work/lawrence-website",
    kind: "case-study",
    image:
      "https://cdn.magicpatterns.com/uploads/1n4MbxHZzc3Vddr7bEKkPF/gettingtolawrence_b.jpg",
    // Only the river below the bridge and shoreline
    motionMask: "linear-gradient(to bottom, transparent 0%, transparent 58%, #000 64%, #000 100%)",
    motionStyle: "water",
  },
  {
    id: "downtown-appleton-reel",
    client: "Lawrence University × Downtown Appleton Inc.",
    title: "A Song for Downtown Appleton",
    description:
      "The influencer video at the center of an integrated campaign I designed for Lawrence University in tandem with Downtown Appleton Inc., bringing both brands into the Appleton community and reaching 400,000+ views.",
    tags: ["Integrated Campaign", "Influencer Marketing"],
    href: "https://www.instagram.com/reel/DSafJfBER_n/",
    kind: "reel",
    image: "https://cdn.magicpatterns.com/uploads/e4CXUxfUKXbNwK4GmeHUxx/Instagram.png",
    stats: [
      { value: "400K+", label: "Views" },
      { value: "10K+", label: "Likes" },
    ],
    credit:
      "I designed the campaign and led the video's production. Lyrics, businesses and photos were chosen together with Downtown.",
    cta: "Watch the reel",
  },
  {
    id: "wooster-enrollment-journey",
    client: "The College of Wooster",
    title: "From First Click to Application",
    description:
      "Connecting paid and organic media, the website and HubSpot into one journey to application.",
    tags: ["Customer Journey", "Martech"],
    href: "/work/wooster-enrollment-journey",
    kind: "case-study",
    image:
      "https://cdn.magicpatterns.com/uploads/ub2qhp2wDRJwDG6ZRL3Uhz/wooster.jpg",
    // Shift the wide crop down so the arched gateway stays in frame
    heroPosition: "center 78%",
    motionMask:
      // Only the bare trees above the rooflines, clear of the tower and wings
      "radial-gradient(ellipse 20% 30% at 6% 6%, #000 60%, transparent 100%), radial-gradient(ellipse 17% 24% at 96% 6%, #000 60%, transparent 100%)",
  },
  {
    id: "lawrence-search-social",
    client: "Lawrence University",
    title: "Making Search and Social Work Together",
    description:
      "Aligning what audiences search for, what they see on social media and how they experience a university online.",
    tags: ["SEO", "Social Media"],
    href: "/work/lawrence-search-social",
    kind: "in-progress",
  },
  {
    id: "more-work",
    client: "More Work",
    title: "Curious? There’s More.",
    description: "Looking for something specific? I probably have a story.",
    tags: ["More Work", "Just Ask"],
    href: "/#contact",
    kind: "invite",
    cta: "Request more",
  },
];
